/**
 * PINK #1 (P1) — modal de PIN de piso (tablet-first, teclado numérico grande).
 * Aparece SOLO en la sesión compartida de tablet (me.isFloor) al ABRIR una orden
 * y al entrar a "Mis tickets". El PIN identifica, NO autoriza: el backend regresa
 * un acting token firmado (5 min) que solo cambia la atribución (opened_by).
 * Sin window.alert: errores inline (regla de la casa). El PIN no pasa por
 * Turnstile; el rate-limit vive en el servidor (5/min, bloqueo 5 min).
 */
import { useState } from 'react';
import { api } from '../lib/api';

export interface FloorPinResult { actingToken: string; userId: string; name: string }

export function FloorPinModal({ title = '¿Quién atiende?', onOk, onClose, dismissable = true }: {
  title?: string;
  onOk: (r: FloorPinResult) => void;
  onClose: () => void;
  /** false = pantalla de BLOQUEO (TREN 1.2): sin ✕/Cancelar y sin backdrop
   *  propio — se pinta como tarjeta inline dentro del lock. */
  dismissable?: boolean;
}) {
  const [pin, setPin] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (value: string) => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const r = (await api('/pos/floor/pin-verify', { method: 'POST', body: { pin: value } })) as FloorPinResult;
      onOk(r);
    } catch (e: any) {
      setErr(e?.body?.message || 'PIN incorrecto');
      setPin('');
      setBusy(false);
    }
  };

  const press = (d: string) => {
    if (busy) return;
    setErr(null);
    const next = (pin + d).slice(0, 4);
    setPin(next);
    if (next.length === 4) void submit(next);
  };

  const card = (
      <div className="w-full max-w-xs rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-serif text-lg text-slate-900">{title}</h3>
          {dismissable && <button className="text-slate-400 hover:text-slate-700" onClick={onClose}>✕</button>}
        </div>
        <p className="mb-3 text-xs text-slate-500">Teclea tu PIN de 4 dígitos. La venta queda a tu nombre.</p>
        <div className="mb-4 flex justify-center gap-3">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={`h-4 w-4 rounded-full border-2 ${i < pin.length ? 'border-slate-900 bg-slate-900' : 'border-slate-300'}`} />
          ))}
        </div>
        {err && <p className="alert-over mb-3 rounded-xl px-3 py-2 text-center text-xs">{err}</p>}
        <div className="grid grid-cols-3 gap-2">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
            <button key={d} type="button" disabled={busy} onClick={() => press(d)}
              className="rounded-xl border border-slate-200 bg-white py-4 text-xl font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-40">
              {d}
            </button>
          ))}
          <button type="button" disabled={busy} onClick={() => { setErr(null); setPin(''); }}
            className="rounded-xl border border-slate-200 bg-white py-4 text-sm font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-40">
            Borrar
          </button>
          <button type="button" disabled={busy} onClick={() => press('0')}
            className="rounded-xl border border-slate-200 bg-white py-4 text-xl font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-40">
            0
          </button>
          {dismissable ? (
            <button type="button" disabled={busy} onClick={onClose}
              className="rounded-xl border border-slate-200 bg-white py-4 text-sm font-medium text-slate-500 hover:bg-slate-50 disabled:opacity-40">
              Cancelar
            </button>
          ) : <span />}
        </div>
        {busy && <p className="mt-3 text-center text-xs text-slate-400">Verificando…</p>}
      </div>
  );

  if (!dismissable) return card; // TREN 1.2 — tarjeta inline del lock (sin backdrop)
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/50 p-4">
      {card}
    </div>
  );
}
